import { FC } from "react";
import {
  AlternativeTransportItineraryStop,
  StopActivity,
  VehicleStatus,
} from "shared/types/alternativeTransport";
import styled, { useTheme } from "styled-components";
import {
  isStopCancelled,
  timeFromKeys,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/utils";
import {
  Flex,
  HStack,
  IconButton,
  TableCell,
  TableRow,
  Text,
  Tooltip,
  VStack,
} from "shared/components/Spor";
import { StopIcon } from "features/CenterContent/shared/StopIcon";
import { BusTimes } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/ItineraryDetails";
import { EditOutline24Icon } from "@vygruppen/spor-icon-react";
import { ColorTheme } from "shared/theme/theme";
import colors from "shared/theme/colors";

const IconCellWrapper = styled(VStack)`
  height: 100%;
  position: relative;
  margin-top: 6px;
  left: 1px;
`;

const Line = styled(Flex)`
  position: absolute;
  margin-top: 17px;
  height: calc(100% + 14px);
  width: 1px;
`;

const EditButtonWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  td:hover & {
    opacity: 1;
  }
`;

const getTooltipLabel = (cancelled: boolean, activity: StopActivity) => {
  if (cancelled) return "Innstilt på stopp";
  switch (activity) {
    case "NONE":
      return "Passerer";
    case "BOARDING":
      return "Påstigning";
    case "DISEMBARKING":
      return "Avstigning";
    case "BOARDING_AND_DISEMBARKING":
      return "På- og avstigning";
  }
};

const getStopColor = (
  cancelled: boolean,
  activity: StopActivity,
  theme: ColorTheme,
) => {
  if (cancelled) return theme.colorIconMain;
  switch (activity) {
    case "BOARDING":
      return colors.primaryGreen;
    case "DISEMBARKING":
      return theme.colorAlarm;
    case "BOARDING_AND_DISEMBARKING":
    case "NONE":
      return theme.colorIconMain;
  }
};

export type BusStopRowProps = {
  stop: AlternativeTransportItineraryStop;
  index: number;
  allStopsLength: number;
  vehicleStatus: VehicleStatus;
  isCurrentStop: boolean;
  onEdit: (stop: AlternativeTransportItineraryStop) => void;
};

export const BusStopRow: FC<BusStopRowProps> = ({
  stop,
  index,
  allStopsLength,
  vehicleStatus,
  isCurrentStop,
  onEdit,
}) => {
  const theme = useTheme();
  const cancelled = isStopCancelled(stop, vehicleStatus);
  const stopColor = getStopColor(cancelled, stop.activity, theme);
  const lineColor = theme.colorIconMain;
  const last = index === allStopsLength - 1;

  const arrivalTimes = {
    actualOrEstimatedTime: timeFromKeys(stop, [
      "actualArrivalTime",
      "estimatedArrivalTime",
    ]),
    scheduledTime: timeFromKeys(stop, ["scheduledArrivalTime"]),
  };

  const departureTimes = {
    actualOrEstimatedTime: timeFromKeys(stop, [
      "actualDepartureTime",
      "estimatedDepartureTime",
    ]),
    scheduledTime: timeFromKeys(stop, ["scheduledDepartureTime"]),
  };

  const tooltipLabel = getTooltipLabel(cancelled, stop.activity);

  return (
    <TableRow
      backgroundColor={isCurrentStop ? theme.colorSelected : undefined}
      outline={`1px solid ${isCurrentStop ? theme.colorOutlineMain : theme.colorOutlineSecondary}`}
    >
      <TableCell>
        <IconCellWrapper>
          <Tooltip label={tooltipLabel} placement="right">
            <Flex>
              <StopIcon
                justPassingThrough={stop.activity === "NONE"}
                isCancelled={cancelled}
                color={stopColor}
                fill
              />
            </Flex>
          </Tooltip>
          {!last && <Line backgroundColor={lineColor} />}
        </IconCellWrapper>
      </TableCell>
      <TableCell>
        <HStack justifyContent="space-between">
          <Text
            variant="xs"
            title={stop.name ?? undefined}
            fontWeight={700}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {stop.name}
          </Text>
        </HStack>
      </TableCell>
      <TableCell>
        <HStack>
          <BusTimes {...arrivalTimes} isCancelled={stop.isArrivalCancelled} />
          <EditButtonWrapper>
            <IconButton
              variant="ghost"
              size="sm"
              icon={<EditOutline24Icon />}
              onClick={() => onEdit(stop)}
              aria-label="Endre tidspunkt for stoppested"
            />
          </EditButtonWrapper>
        </HStack>
      </TableCell>
      <TableCell>
        <HStack>
          <BusTimes {...departureTimes} isCancelled={stop.isArrivalCancelled} />
          <EditButtonWrapper>
            <IconButton
              variant="ghost"
              size="sm"
              icon={<EditOutline24Icon />}
              onClick={() => onEdit(stop)}
              aria-label="Endre tidspunkt for stoppested"
            />
          </EditButtonWrapper>
        </HStack>
      </TableCell>
    </TableRow>
  );
};
